<template>
  <div v-if="!isBrandPage" class="m-4 lg:container lg:m-4 lg:mt-8">
    <CMSContentPage :slug="slug" data-testid="content-page">
      <template #loading>
        <SFContentPageSkeletonLoader />
      </template>
    </CMSContentPage>
  </div>
  <BrandsPage v-else :gender="gender" />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { definePageMeta, useSeoMeta, useRoute } from '#imports'
import CMSContentPage from '#storefront-cms/components/ContentPage.vue'
import SFContentPageSkeletonLoader from '~/components/SFContentPageSkeletonLoader.vue'
import BrandsPage from '~/components/pages/SFBrandsListPageContent.vue'

definePageMeta({
  middleware: ['brand-content-page-type'],
})

const route = useRoute()
const { t } = useI18n()
const brands = t('brands_page.title')

const slugArray = computed(() => route?.params?.slug as string[])

const lastSegment = computed(() => slugArray.value.at(-1))

const isBrandPage = computed(() => {
  return (
    lastSegment.value === brands ||
    lastSegment.value?.endsWith('-brands') ||
    lastSegment.value?.endsWith('-marki')
  )
})

const gender = computed(() => {
  if (
    lastSegment.value &&
    (lastSegment.value.endsWith('-brands') ||
      lastSegment.value.endsWith('-marki')) &&
    lastSegment.value !== 'brands'
  ) {
    return lastSegment.value.replace('-brands', '').replace('-marki', '') // "men", "women", "kids"
  }
  return null
})

// Handle both /s/slug and /s/something/slug patterns
const slug = computed(() => {
  const pathSegments = route.path.split('/').slice(2) // Remove empty string and 's'
  return `/${pathSegments.join('/')}`
})

const seoTitle = computed(() => {
  if (isBrandPage.value) {
    if (!gender.value) {
      return brands
    }
    // capitalize first letter of gender
    const g = gender.value.charAt(0).toUpperCase() + gender.value.slice(1)
    return `${g} ${brands}`
  }

  // for other CMS content pages: capitalize slug
  const slugLabel = slug.value.replace('/', '')
  return `${slugLabel.charAt(0).toUpperCase() + slugLabel.slice(1)}`
})

useSeoMeta({ robots: 'index,follow', title: seoTitle })

defineOptions({ name: 'ContentPage' })
</script>

<style lang="css">
div[data-testid='content-page'] #cms-content p,
div[data-testid='content-page'] #cms-content ul,
div[data-testid='content-page'] #cms-content ol {
  @apply mb-6;
}
</style>
